import type {
  BrandMaterial,
  BuilderDesignConfig,
  BuilderRulesConfig,
  ChatConfigExportJob,
  ChatConfigExportStatus,
  ChatConfigImportJob,
  ChatConfigImportStatus,
  ChatConfiguration,
  ChatConfigurationAsset,
  ChatConfigurationResponse,
  ChatConfigurationVersion,
  DataSource,
  DesignConfig,
  PreviewData,
  ProductCollection,
  ProductConfig,
  PublishConfigurationResponse,
  RulesConfig,
  UtmParameter,
} from '@/stores/chatBuilderStore'
import {
  type ApiResponse,
  apiClient,
  handleApiError,
  handleApiResponse,
} from './api'
import { appcuesService } from './appcues'
import type {
  CompactRulesRequest,
  CompactRulesResponse,
  DesignRestoreRequest,
} from './chatBuilder.types'

interface CreateConfigurationRequest {
  name?: string
  description?: string
  deploymentType?: string
  enabledDeploymentTypes?: string[]
  // Partial: this is a create payload — the Rails backend fills in defaults
  // for anything omitted, so `{}` (all-defaults) is a valid request body.
  rulesConfig?: Partial<BuilderRulesConfig>
  productConfig?: Partial<ProductConfig>
  designConfig?: Partial<BuilderDesignConfig>
  brandLogoUrl?: string
}

interface GenerateRulesRequest {
  instructions: string
  collectionId?: string
}

interface GenerateRulesResponse {
  success: boolean
  data: {
    // AI-generated rules/design are always concrete builder shapes:
    // rulesConfig feeds RulesTab.tsx's useState<Rule[]>, designConfig.colors
    // feeds buildConfigurationFromGeneratedRules's palette fields.
    rulesConfig: BuilderRulesConfig
    agentName?: string
    designConfig?: BuilderDesignConfig
    logoUrl?: string
  }
}

/**
 * Partial update request for configurations.
 * All nested properties are optional to support partial updates.
 *
 * designConfig/rulesConfig use the Builder* variants (not the plain shared
 * DesignConfig/RulesConfig) because the builder UI reads/writes fields the
 * published contract doesn't type: color palettes + icon URL snake-survivors
 * (ColorSection.tsx, IconsSection.tsx) and rule attachments (AddRuleModal.tsx).
 */
interface UpdateConfigurationRequest {
  section?: string
  data?: RulesConfig | ProductConfig | DesignConfig
  name?: string
  description?: string
  rulesConfig?: Partial<BuilderRulesConfig>
  productConfig?: Partial<ProductConfig>
  designConfig?: {
    styles?: Partial<BuilderDesignConfig['styles']>
    colors?: Partial<BuilderDesignConfig['colors']>
    typography?: Partial<BuilderDesignConfig['typography']>
    productTiles?: Partial<BuilderDesignConfig['productTiles']>
    icons?: Partial<BuilderDesignConfig['icons']>
    disclaimer?: Partial<BuilderDesignConfig['disclaimer']>
    logo?: Partial<BuilderDesignConfig['logo']>
    text?: Partial<BuilderDesignConfig['text']>
    avatar?: Partial<BuilderDesignConfig['avatar']>
    sendButton?: Partial<BuilderDesignConfig['sendButton']>
    background?: Partial<BuilderDesignConfig['background']>
    launcher?: Partial<NonNullable<BuilderDesignConfig['launcher']>>
  }
  utmParams?: UtmParameter[]
  deploymentType?: string
  enabledDeploymentTypes?: string[]
  websiteUrl?: string
  brandLogoUrl?: string
  logoAssetId?: string
  aiMessengerIconAssetId?: string
  sendButtonIconAssetId?: string
  backgroundImageAssetId?: string
  faviconAssetId?: string | null
  launcherAvatarAssetId?: string | null
}

interface PublishConfigurationRequest {
  description: string
}

interface ConfigurationListResponse {
  configurations: ChatConfiguration[]
  total: number
  page: number
  perPage: number
}

interface ProductCollectionsResponse {
  collections: ProductCollection[]
  total: number
}

interface BrandMaterialsResponse {
  materials: BrandMaterial[]
  total: number
}

interface ConfigurationVersionsResponse {
  versions: ChatConfigurationVersion[]
  total: number
}

interface SampleProductsResponse {
  products: Array<{
    id: string
    name: string
    price: number
    imageUrl?: string
    description?: string
  }>
  total: number
}

interface SchemaVersionsResponse {
  schemas: Array<{
    version: string
    createdAt: string
    deprecatedAt?: string
    schema: Record<string, unknown>
  }>
  total: number
}

interface SchemaVersionResponse {
  version: string
  schema: Record<string, unknown>
  createdAt: string
  deprecatedAt?: string
}

class ChatBuilderService {
  private baseURL = '/chat_configurations'

  /**
   * Get all chat configurations
   */
  async getConfigurations(params?: {
    status?: string
    page?: number
    perPage?: number
  }): Promise<ConfigurationListResponse> {
    try {
      const response = await apiClient.get(this.baseURL, { params })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Get a specific chat configuration
   */
  async getConfiguration(id: string): Promise<ChatConfigurationResponse> {
    try {
      const response = await apiClient.get(`${this.baseURL}/${id}`)

      // Check if response indicates org switch is required
      if (response.data.requiresOrgSwitch) {
        return {
          success: true,
          requiresOrgSwitch: true,
          targetOrganization: response.data.targetOrganization,
          message: response.data.message,
        }
      }

      // Normal response - return the configuration data
      return {
        success: true,
        data: handleApiResponse(response),
      }
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Create a new chat configuration
   */
  async createConfiguration(
    data: CreateConfigurationRequest
  ): Promise<ChatConfiguration> {
    try {
      const response = await apiClient.post(this.baseURL, {
        chatConfiguration: data,
      })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  }

  async createGuidedConfiguration(
    data: CreateConfigurationRequest
  ): Promise<ChatConfiguration> {
    try {
      const response = await apiClient.post(`${this.baseURL}/guided`, {
        chatConfiguration: data,
      })

      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Update chat configuration (full or section-specific)
   */
  async updateConfiguration(
    id: string,
    data: UpdateConfigurationRequest
  ): Promise<ChatConfiguration> {
    try {
      const response = await apiClient.patch(`${this.baseURL}/${id}`, data)

      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Update specific configuration section (partial — only changed fields).
   */
  async updateConfigurationSection(
    id: string,
    section: 'rulesConfig' | 'productConfig' | 'designConfig',
    data:
      | Partial<BuilderRulesConfig>
      | Partial<ProductConfig>
      | Partial<BuilderDesignConfig>
  ): Promise<ChatConfiguration> {
    try {
      const response = await apiClient.patch(`${this.baseURL}/${id}`, {
        section,
        data,
      })
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Rename a chat configuration
   */
  async renameConfiguration(
    id: string,
    name: string
  ): Promise<ChatConfiguration> {
    try {
      const response = await apiClient.patch(`${this.baseURL}/${id}`, {
        name,
      })
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Publish a chat configuration
   */
  async publishConfiguration(
    id: string,
    description: string
  ): Promise<PublishConfigurationResponse> {
    try {
      const response = await apiClient.post(`${this.baseURL}/${id}/publish`, {
        description,
      })

      appcuesService.track('Agent Published')

      // Return the full response data to access launch_url and other fields
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Delete a chat configuration
   */
  async deleteConfiguration(id: string): Promise<void> {
    try {
      await apiClient.delete(`${this.baseURL}/${id}`)
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Get configuration versions
   */
  async getConfigurationVersions(
    id: string
  ): Promise<ConfigurationVersionsResponse> {
    try {
      const response = await apiClient.get(`${this.baseURL}/${id}/versions`)
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error)
    }
  }

  async restoreDesign(
    id: string,
    request: DesignRestoreRequest
  ): Promise<ChatConfiguration> {
    try {
      const response = await apiClient.post(
        `${this.baseURL}/${id}/design_restore`,
        request
      )

      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Rollback to a specific version
   */
  async rollbackConfiguration(
    id: string,
    versionId: string
  ): Promise<ChatConfiguration> {
    try {
      const response = await apiClient.post(`${this.baseURL}/${id}/rollback`, {
        versionId: versionId,
      })
      const result = handleApiResponse(response) as {
        data?: ChatConfiguration
        [key: string]: any
      }

      // Extract the configuration data from the success response
      return result.data || (result as ChatConfiguration)
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Discard unpublished changes (revert to live version)
   */
  async discardChanges(id: string): Promise<ChatConfiguration> {
    try {
      const response = await apiClient.post(
        `${this.baseURL}/${id}/discard_changes`
      )
      const result = handleApiResponse(response) as {
        data?: ChatConfiguration
        [key: string]: any
      }

      // Extract the configuration data from the success response
      return result.data || (result as ChatConfiguration)
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Get product collections for organization
   */
  async getProductCollections(params?: {
    status?: string
    page?: number
    perPage?: number
  }): Promise<ProductCollection[]> {
    try {
      // Get organization ID from auth store
      const authData = localStorage.getItem('auth-storage')
      if (!authData) throw new Error('No organization context')

      const parsed = JSON.parse(authData)
      const organizationId = parsed.state?.organization?.id

      if (!organizationId) throw new Error('No organization ID found')

      const response = await apiClient.get(
        `/organizations/${organizationId}/chat_builder_data/product_collections`,
        { params }
      )
      const result = response.data
      return result.collections || []
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Get brand materials for organization
   */
  async getBrandMaterials(params?: {
    materialType?: string
    sourceType?: string
    search?: string
  }): Promise<BrandMaterial[]> {
    try {
      // Get organization ID from auth store
      const authData = localStorage.getItem('auth-storage')
      if (!authData) throw new Error('No organization context')

      const parsed = JSON.parse(authData)
      const organizationId = parsed.state?.organization?.id

      if (!organizationId) throw new Error('No organization ID found')

      const response = await apiClient.get(
        `/organizations/${organizationId}/chat_builder_data/brand_materials`,
        { params }
      )
      const result = response.data
      return result.materials || []
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Get preview data for configuration
   */
  async getPreviewData(
    id: string,
    params?: { limit?: number }
  ): Promise<PreviewData> {
    try {
      const response = await apiClient.get(
        `${this.baseURL}/${id}/preview_data`,
        { params }
      )
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Get sample products for configuration
   */
  async getSampleProducts(
    id: string,
    params?: { limit?: number }
  ): Promise<SampleProductsResponse> {
    try {
      const response = await apiClient.get(
        `${this.baseURL}/${id}/sample_products`,
        { params }
      )
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Upload asset for configuration
   */
  async uploadAsset(
    configId: string,
    assetType: 'ai_messenger_icon' | 'send_button_icon',
    file: File
  ): Promise<ChatConfigurationAsset> {
    try {
      const formData = new FormData()
      formData.append('asset_type', assetType)
      formData.append('file', file)

      const response = await apiClient.post(
        `${this.baseURL}/${configId}/assets`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Delete asset from configuration
   */
  async deleteAsset(configId: string, assetTypeOrId: string): Promise<void> {
    try {
      await apiClient.delete(
        `${this.baseURL}/${configId}/assets/${assetTypeOrId}`
      )
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Get all assets for configuration
   */
  async getConfigurationAssets(
    configId: string,
    params?: { assetType?: string }
  ): Promise<ChatConfigurationAsset[]> {
    try {
      const response = await apiClient.get(
        `${this.baseURL}/${configId}/assets`,
        { params }
      )
      const result = handleApiResponse(response) as {
        assets?: ChatConfigurationAsset[]
      }
      return result.assets || []
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Get available schema versions
   */
  async getSchemaVersions(params?: {
    includeDeprecated?: boolean
  }): Promise<SchemaVersionsResponse> {
    try {
      const response = await apiClient.get('/chat_configuration_schemas', {
        params,
      })
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Get specific schema version
   */
  async getSchemaVersion(version: string): Promise<SchemaVersionResponse> {
    try {
      const response = await apiClient.get(
        `/chat_configuration_schemas/${version}`
      )
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Generate rules using AI based on website content and instructions
   */
  async generateRules(
    request: GenerateRulesRequest
  ): Promise<GenerateRulesResponse> {
    try {
      const response = await apiClient.post(
        `${this.baseURL}/generate_rules`,
        request,
        { timeout: 120000 }
      )
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Ask the AI which rules overlap enough to merge. Returns proposals only;
   * nothing is saved.
   */
  async compactRules(
    request: CompactRulesRequest
  ): Promise<CompactRulesResponse> {
    try {
      const response = await apiClient.post(
        `${this.baseURL}/compact_rules`,
        request,
        { timeout: 120000 }
      )

      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Kick off an async export. The catalog serialization + S3 write happens in a
   * background job; returns a job id to poll with getExportStatus.
   */
  async startExport(id: string): Promise<{ jobId: string }> {
    try {
      const response = await apiClient.post<
        ApiResponse<{ jobId: string; status: ChatConfigExportStatus }>
      >(`${this.baseURL}/${id}/export`)
      const { jobId } = handleApiResponse(response)
      return { jobId }
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Poll the status of an export job. On 'completed' the payload carries a
   * short-lived presigned S3 download URL the browser can fetch directly.
   */
  async getExportStatus(
    id: string,
    jobId: string
  ): Promise<ChatConfigExportJob> {
    try {
      const response = await apiClient.get<ChatConfigExportJob>(
        `${this.baseURL}/${id}/export_status/${jobId}`
      )
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Kick off an async import from a JSON file. The 50MB upload is staged to S3
   * and the parse + cross-DB insert + image attach happen in a background job;
   * returns a job id to poll with getImportStatus.
   *
   * The upload leg can exceed the global 30s axios timeout on a large file even
   * though the request now returns fast, so this call bumps the timeout. The
   * Content-Type is left unset so the interceptor supplies the multipart
   * boundary itself.
   */
  async startImport(file: File): Promise<{ jobId: string }> {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await apiClient.post<
        ApiResponse<{ jobId: string; status: ChatConfigImportStatus }>
      >(`${this.baseURL}/import`, formData, { timeout: 120000 })
      const { jobId } = handleApiResponse(response)
      return { jobId }
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Poll the status of an import job. On 'completed' the payload carries the new
   * configId to navigate to; progress counters drive the client progress bar.
   */
  async getImportStatus(jobId: string): Promise<ChatConfigImportJob> {
    try {
      const response = await apiClient.get<{
        jobId: string
        status: ChatConfigImportStatus
        productsTotal?: number
        productsDone?: number
        imagesTotal?: number
        imagesDone?: number
        chatConfigurationId?: string
        error?: string
      }>(`${this.baseURL}/import_status/${jobId}`)

      const data = response.data

      return {
        jobId: data.jobId,
        status: data.status,
        configId: data.chatConfigurationId,
        error: data.error,
        productsTotal: data.productsTotal,
        productsDone: data.productsDone,
        imagesTotal: data.imagesTotal,
        imagesDone: data.imagesDone,
      }
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Validate file for upload
   */
  async validateFile(file: File): Promise<boolean> {
    // Client-side validation before upload
    const maxSize = 32 * 1024 // 32KB
    const allowedTypes = [
      'image/svg+xml',
      'image/png',
      'image/jpeg',
      'image/jpg',
    ]

    if (file.size > maxSize) {
      throw new Error('File size must be less than 32KB')
    }

    if (!allowedTypes.includes(file.type)) {
      throw new Error('Only SVG, PNG, and JPEG files are allowed')
    }

    return true
  }

  // Data Sources

  async getDataSources(configId: string): Promise<DataSource[]> {
    const response = await apiClient.get<{
      success: boolean
      data: DataSource[]
    }>(`${this.baseURL}/${configId}/data_sources`)
    return response.data.data || []
  }

  async addDataSource(configId: string, url: string): Promise<DataSource> {
    const response = await apiClient.post<{
      success: boolean
      data: DataSource
    }>(`${this.baseURL}/${configId}/data_sources`, { url })
    return response.data.data
  }

  async removeDataSource(configId: string, sourceId: string): Promise<void> {
    await apiClient.delete(
      `${this.baseURL}/${configId}/data_sources/${sourceId}`
    )
  }

  async toggleDataSource(
    configId: string,
    sourceId: string,
    enabled: boolean
  ): Promise<DataSource> {
    const response = await apiClient.patch<{
      success: boolean
      data: DataSource
    }>(`${this.baseURL}/${configId}/data_sources/${sourceId}`, { enabled })
    return response.data.data
  }

  async rescrapeDataSource(
    configId: string,
    sourceId: string
  ): Promise<DataSource> {
    const response = await apiClient.post<{
      success: boolean
      data: DataSource
    }>(`${this.baseURL}/${configId}/data_sources/${sourceId}/rescrape`)
    return response.data.data
  }

  async uploadPdfDataSource(configId: string, file: File): Promise<DataSource> {
    const formData = new FormData()

    formData.append('file', file)

    const response = await apiClient.post<{
      success: boolean
      data: DataSource
    }>(`${this.baseURL}/${configId}/data_sources/upload_pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data.data
  }
}

export const chatBuilderService = new ChatBuilderService()

export type {
  BrandMaterialsResponse,
  CompactRulesRequest,
  CompactRulesResponse,
  ConfigurationListResponse,
  CreateConfigurationRequest,
  GenerateRulesRequest,
  GenerateRulesResponse,
  ProductCollectionsResponse,
  PublishConfigurationRequest,
  UpdateConfigurationRequest,
}
